// These need to be here to avoid circular dependencies

export const ROUTE_ID = ":id";

export const AI_ROUTES = {
  Ai: "/admin/ai",
  AiWorkflowBuilder: "/admin/ai/workflow-builder",
  AiWorkflowBuilderWithId: "/admin/ai/workflow-builder/:workflowId",
  AiAgents: "/admin/ai/agents",
  AiAgentDetail: "/admin/ai/agents/:agentName",
  AiTestCategorizations: "/admin/ai/test-categorizations",
  AiBotReview: "/admin/ai/bot-review",
  AiExperimentClassifications: "/admin/ai/experiment-classifications",
  AiExperimentClassificationDetail:
    "/admin/ai/experiment-classifications/experience/:experienceId",
  // The whole admin Agentic Logic surface — list, rule creation, and the rule
  // workspace — is this one route; the view is the `rule` query param. The two
  // paths below are the pre-query-param shapes, kept only as redirects.
  AiAgenticLogic: "/admin/ai/agentic-logic",
  AiAgenticLogicNewRule: "/admin/ai/agentic-logic/new",
  AiAgenticLogicRule: "/admin/ai/agentic-logic/:ruleId",
};

export const ADMIN_ROUTES = {
  AdminOverview: "/admin/overview",
  AdminOrgs: "/admin/orgs",
  Agencies: "/admin/agencies",
  AdminAgencyDetail: "/admin/agencies/:agencyId",
  AgenticLogicStats: "/admin/agentic-logic/stats",
  ShippingProviderStats: "/admin/shipping-providers/stats",
  AiPatchReview: "/ai/patch-review/:sessionId",
  AiPylonTicketAnalysis: "/ai/pylon/:issueId",
  AiShiftAnalysis: "/ai/shift/:linearIssueId",
  BillingWorkflowTrace: "/admin/billing-workflow-trace",
  BillingMetrics: "/admin/billing/metrics",
  BillingMetricsCommand: "/admin/billing/metrics/commands/:commandId",
  AdminOrgDetail: "/admin/orgs/:orgId",
  BillingSupportCommand: "/admin/billing/support/commands/:commandId",
  DemoStoreManagement: "/admin/demo-stores",
  FeatureFlags: "/admin/feature-flags",
  KnowledgeManagement: "/admin/knowledge",
  KnowledgeSearch: "/admin/knowledge/search",
  KnowledgeDocument: "/admin/knowledge/:documentId",
  QaBotReview: "/admin/qa-bot-review",
  PylonTicketAnalysis: "/admin/pylon/:issueId",
  ShiftAnalysis: "/admin/shift/:linearIssueId",
  Regression: "/admin/regression",
  PatchReview: "/admin/patch-review/:sessionId",
  SlackIntegrations: "/admin/slack-integrations",
  SlackIntegrationDetail: "/admin/slack-integrations/:integrationId",
};

export const WIDGET_ROUTES = {
  WidgetLibrary_Experiences: "/widget-library/experiences",
  WidgetLibrary_Sitewide: "/widget-library/sitewide",
};

export const GLOBAL_STYLES_ROUTES = {
  GlobalStyles_Experiences: "/global-styles/experiences",
  GlobalStyles_Sitewide: "/global-styles/sitewide",
};

export const ONBOARDING_ROUTES = {
  GetStarted: "/get-started",
} as const;

export const GENERAL_ROUTES = {
  Index: "/",
  Home: "/home",
  ConfigHandler: "/config-handler",
  Readme_Login: "/readme/login",
  Integrations: "/integrations",
  GoogleIntegrationCallback: "/integrations/google-callback",
  NotSubscribed: "/not-subscribed",
  Performance: "/performance",
  Logout: "/logout",
  IntelligemsAi: "/intelligems-ai",
  ConfigCompare: "/config-compare",
  EventsManager: "/events-manager",
  // The whole merchant Agentic Logic surface — list, rule creation, and the
  // rule workspace — is this one route; the view is the `rule` query param.
  // The two paths below are the pre-query-param shapes, kept only as
  // redirects.
  Rules: "/rules",
  NewRule: "/rules/new",
  Rule: "/rules/:ruleId",
  ManagePlans: "/manage-plans",
  Offers: "/offers",
  SitewideAnalytics: "/sitewide-analytics",
  SitewideAnalytics_Id: "/sitewide-analytics/:id",
  SitewideAnalyticsAudience_Id: "/sitewide-analytics/:id/audience",
  SitewideAnalyticsConversion_Id: "/sitewide-analytics/:id/conversion",
  SitewideAnalyticsHolidayBenchmark_Id:
    "/sitewide-analytics/:id/holiday-benchmark",
  SitewideAnalyticsOrderDistribution_Id:
    "/sitewide-analytics/:id/order-distribution",
  SitewideAnalyticsPerformance_Id: "/sitewide-analytics/:id/performance",
  GetHelp: "/get-help",
  GetInspired: "/inspiration",
  GetInspiredDoc: "/inspiration/docs/*",
  GetInspiredPageById: "/inspiration/docs/page/:pageId",
  GetInspiredYouTube: "/inspiration/youtube/:videoId",
  ReverseProxyTest: "/reverse-proxy-test",
  QaReview: "/:orgId/qa-review/:runId",
} as const;

/** Builds the path to the standalone QA review page for a given run. */
export const getQaReviewPath = (orgId: string, runId: string) =>
  `/${orgId}/qa-review/${runId}`;

export const getBillingMetricsCommandPath = (commandId: string) =>
  `/admin/billing/metrics/commands/${commandId}`;

export const getAdminOrgPath = (orgId: string) => `/admin/orgs/${orgId}`;

export const getAdminAgencyPath = (agencyId: string) =>
  `/admin/agencies/${agencyId}`;

export const getBillingSupportCommandPath = (commandId: string) =>
  `/admin/billing/support/commands/${commandId}`;

export const SETTINGS_ROUTES = {
  Settings: "/settings",
} as const;

export const PERSONALIZATION_ROUTES = {
  Personalizations: "/personalizations",
  Personalization: "/personalization",
  Personalization_Id: "/personalization/:id?",
} as const;

export const EXPERIENCE_ROUTES = {
  Experiences: "/experiences", // TODO - CP URGENT NEED TO REROUTE THIS
  Experiences_Content: "/content",
  Experiences_Merchandising: "/merchandising",
  Experiences_Pricing: "/pricing",
  Experiences_Shipping: "/shipping",
  Experiences_Checkout: "/checkout",
  Experiences_PostPurchaseModule: "/post-purchase",
  Experiences_Offers: "/experiences/offers",
  Experiences_SiteContent: "/experiences/site-content",
  Experiences_CheckoutBlocks_Old: "/experiences/checkout-blocks",
  Experiences_CheckoutBlocks: "/experiences/checkout",
  Experiences_PostPurchase: "/experiences/post-purchase",
} as const;

export const EXPERIMENT_ROUTES = {
  Experiments: "/experiments",
  Experiment: "/experiment",
  Experiment_Id: "/experiment/:id?",
} as const;

export const OLD_EXPERIMENT_ROUTES = {
  Experiences: "/experiences",
  Compare: "/compare",
  Compare_Id: "/compare/:id",
  ContentTest: "/content-test",
  ContentTest_Id: "/content-test/:id?",
  PriceTest: "/price-test",
  PriceTest_Id: "/price-test/:id?",
  ShippingTest: "/shipping-test",
  ShippingTest_Id: "/shipping-test/:id?",
  OfferTest: "/offer-test",
  OfferTest_Id: "/offer-test/:id?",
  DuplicateProducts: "/duplicate-products",
  DuplicateProducts_Id: "/duplicate-products/:id",
} as const;

export const OLD_DASHBOARD_ROUTES = {
  Dashboard: "/dashboard",
  Dashboard_Overview: "/dashboard/overview",
  Dashboard_Overview_Id: "/dashboard/overview/:id",
  Dashboard_Profit: "/dashboard/profit",
  Dashboard_Profit_Id: "/dashboard/profit/:id",
  Dashboard_Revenue: "/dashboard/revenue",
  Dashboard_Revenue_Id: "/dashboard/revenue/:id",
  Dashboard_Shipping: "/dashboard/shipping",
  Dashboard_Shipping_Id: "/dashboard/shipping/:id",
  Dashboard_Visitors: "/dashboard/visitors",
  Dashboard_Visitors_Id: "/dashboard/visitors/:id",
  Dashboard_Orders: "/dashboard/orders",
  Dashboard_Orders_Id: "/dashboard/orders/:id",
  Dashboard_Discounts: "/dashboard/discounts",
  Dashboard_Discounts_Id: "/dashboard/discounts/:id",
  Dashboard_ProductGroups: "/dashboard/product-groups",
  Dashboard_ProductGroups_Id: "/dashboard/product-groups/:id",
  Dashboard_Subscriptions: "/dashboard/subscriptions",
  Dashboard_Subscriptions_Id: "/dashboard/subscriptions/:id",
  Dashboard_Data_Reconciliation: "/dashboard/data-reconciliation",
  Dashboard_Data_Reconciliation_Id: "/dashboard/data-reconciliation/:id",
  Dashboard_Trend: "/dashboard/trend",
  Dashboard_StatSig: "/dashboard/statSig",
  Dashboard_AlwaysOn: "/dashboard/sitewide",
} as const;

export const PUBLIC_ROUTES = {
  Login: "/login",
  Share_Id: "/share/:id",
  Install: "/install",
} as const;

export const FALLBACK_TITLE = "Intelligems";

/**
 * Document title per route, colocated with the route definitions, keyed by
 * path pattern.
 */
export const ROUTE_TITLES: Record<string, string> = {
  // General
  [GENERAL_ROUTES.Index]: "Home",
  [GENERAL_ROUTES.Home]: "Home",
  [GENERAL_ROUTES.ConfigHandler]: "Config Handler",
  [GENERAL_ROUTES.Readme_Login]: "ReadMe Login",
  [GENERAL_ROUTES.Integrations]: "Integrations",
  [GENERAL_ROUTES.GoogleIntegrationCallback]: "Google Integration",
  [GENERAL_ROUTES.NotSubscribed]: "Not Subscribed",
  [GENERAL_ROUTES.Performance]: "Performance",
  [GENERAL_ROUTES.Logout]: "Logout",
  [GENERAL_ROUTES.IntelligemsAi]: "Intelligems AI",
  [GENERAL_ROUTES.ConfigCompare]: "Config Compare",
  [GENERAL_ROUTES.EventsManager]: "Events Manager",
  [GENERAL_ROUTES.Rules]: "Rules",
  [GENERAL_ROUTES.NewRule]: "Rules",
  [GENERAL_ROUTES.Rule]: "Rules",
  [GENERAL_ROUTES.ManagePlans]: "Manage Plans",
  [GENERAL_ROUTES.Offers]: "Offers",
  [GENERAL_ROUTES.SitewideAnalytics]: "Sitewide Analytics",
  [GENERAL_ROUTES.SitewideAnalytics_Id]: "Sitewide Analytics",
  [GENERAL_ROUTES.SitewideAnalyticsAudience_Id]:
    "Sitewide Analytics - Audience",
  [GENERAL_ROUTES.SitewideAnalyticsConversion_Id]:
    "Sitewide Analytics - Conversion",
  [GENERAL_ROUTES.SitewideAnalyticsHolidayBenchmark_Id]:
    "Sitewide Analytics - Holiday Benchmark",
  [GENERAL_ROUTES.SitewideAnalyticsOrderDistribution_Id]:
    "Sitewide Analytics - Order Distribution",
  [GENERAL_ROUTES.SitewideAnalyticsPerformance_Id]:
    "Sitewide Analytics - Performance",
  [GENERAL_ROUTES.GetHelp]: "Get Help",
  [GENERAL_ROUTES.GetInspired]: "Get Inspired",
  [GENERAL_ROUTES.GetInspiredDoc]: "Get Inspired",
  [GENERAL_ROUTES.GetInspiredPageById]: "Get Inspired",
  [GENERAL_ROUTES.GetInspiredYouTube]: "Get Inspired",
  [GENERAL_ROUTES.ReverseProxyTest]: "Reverse Proxy Test",

  // Settings
  [SETTINGS_ROUTES.Settings]: "Settings",

  // Personalizations
  [PERSONALIZATION_ROUTES.Personalizations]: "Personalizations",
  [PERSONALIZATION_ROUTES.Personalization]: "Personalization",
  [PERSONALIZATION_ROUTES.Personalization_Id]: "Personalization",

  // Experiences
  [EXPERIENCE_ROUTES.Experiences]: "Experiences",
  [EXPERIENCE_ROUTES.Experiences_Content]: "Content",
  [EXPERIENCE_ROUTES.Experiences_Merchandising]: "Merchandising",
  [EXPERIENCE_ROUTES.Experiences_Pricing]: "Pricing",
  [EXPERIENCE_ROUTES.Experiences_Shipping]: "Shipping",
  [EXPERIENCE_ROUTES.Experiences_Checkout]: "Checkout",
  [EXPERIENCE_ROUTES.Experiences_PostPurchaseModule]: "Post-Purchase",
  [EXPERIENCE_ROUTES.Experiences_Offers]: "Offers",
  [EXPERIENCE_ROUTES.Experiences_SiteContent]: "Site Content",
  [EXPERIENCE_ROUTES.Experiences_CheckoutBlocks_Old]: "Checkout Blocks",
  [EXPERIENCE_ROUTES.Experiences_CheckoutBlocks]: "Checkout",
  [EXPERIENCE_ROUTES.Experiences_PostPurchase]: "Post-Purchase",

  // Experiments
  [EXPERIMENT_ROUTES.Experiments]: "Tests",
  [EXPERIMENT_ROUTES.Experiment]: "Test",
  [EXPERIMENT_ROUTES.Experiment_Id]: "Test",

  // Widgets
  [WIDGET_ROUTES.WidgetLibrary_Experiences]: "Widget Library",
  [WIDGET_ROUTES.WidgetLibrary_Sitewide]: "Widget Library",

  // Global styles
  [GLOBAL_STYLES_ROUTES.GlobalStyles_Experiences]: "Global Styles",
  [GLOBAL_STYLES_ROUTES.GlobalStyles_Sitewide]: "Global Styles",

  // Old experiment routes (legacy)
  [OLD_EXPERIMENT_ROUTES.ContentTest]: "Content Test",
  [OLD_EXPERIMENT_ROUTES.ContentTest_Id]: "Content Test",
  [OLD_EXPERIMENT_ROUTES.PriceTest]: "Price Test",
  [OLD_EXPERIMENT_ROUTES.PriceTest_Id]: "Price Test",
  [OLD_EXPERIMENT_ROUTES.ShippingTest]: "Shipping Test",
  [OLD_EXPERIMENT_ROUTES.ShippingTest_Id]: "Shipping Test",
  [OLD_EXPERIMENT_ROUTES.OfferTest]: "Offer Test",
  [OLD_EXPERIMENT_ROUTES.OfferTest_Id]: "Offer Test",
  [OLD_EXPERIMENT_ROUTES.DuplicateProducts]: "Duplicate Products",
  [OLD_EXPERIMENT_ROUTES.DuplicateProducts_Id]: "Duplicate Products",

  // Old dashboard routes (legacy)
  [OLD_DASHBOARD_ROUTES.Dashboard]: "Dashboard",
  [OLD_DASHBOARD_ROUTES.Dashboard_Overview]: "Overview",
  [OLD_DASHBOARD_ROUTES.Dashboard_Overview_Id]: "Overview",
  [OLD_DASHBOARD_ROUTES.Dashboard_Profit]: "Profit",
  [OLD_DASHBOARD_ROUTES.Dashboard_Profit_Id]: "Profit",
  [OLD_DASHBOARD_ROUTES.Dashboard_Revenue]: "Revenue",
  [OLD_DASHBOARD_ROUTES.Dashboard_Revenue_Id]: "Revenue",
  [OLD_DASHBOARD_ROUTES.Dashboard_Shipping]: "Shipping",
  [OLD_DASHBOARD_ROUTES.Dashboard_Shipping_Id]: "Shipping",
  [OLD_DASHBOARD_ROUTES.Dashboard_Visitors]: "Visitors",
  [OLD_DASHBOARD_ROUTES.Dashboard_Visitors_Id]: "Visitors",
  [OLD_DASHBOARD_ROUTES.Dashboard_Orders]: "Orders",
  [OLD_DASHBOARD_ROUTES.Dashboard_Orders_Id]: "Orders",
  [OLD_DASHBOARD_ROUTES.Dashboard_Discounts]: "Discounts",
  [OLD_DASHBOARD_ROUTES.Dashboard_Discounts_Id]: "Discounts",
  [OLD_DASHBOARD_ROUTES.Dashboard_ProductGroups]: "Product Groups",
  [OLD_DASHBOARD_ROUTES.Dashboard_ProductGroups_Id]: "Product Groups",
  [OLD_DASHBOARD_ROUTES.Dashboard_Subscriptions]: "Subscriptions",
  [OLD_DASHBOARD_ROUTES.Dashboard_Subscriptions_Id]: "Subscriptions",
  [OLD_DASHBOARD_ROUTES.Dashboard_Data_Reconciliation]: "Data Reconciliation",
  [OLD_DASHBOARD_ROUTES.Dashboard_Data_Reconciliation_Id]:
    "Data Reconciliation",
  [OLD_DASHBOARD_ROUTES.Dashboard_Trend]: "Trend",
  [OLD_DASHBOARD_ROUTES.Dashboard_StatSig]: "StatSig",
  [OLD_DASHBOARD_ROUTES.Dashboard_AlwaysOn]: "Sitewide",

  // Admin
  [ADMIN_ROUTES.AdminOverview]: "Admin Overview",
  [ADMIN_ROUTES.AdminOrgs]: "Organizations",
  [ADMIN_ROUTES.DemoStoreManagement]: "Demo Stores",
  [ADMIN_ROUTES.Agencies]: "Agencies",
  [ADMIN_ROUTES.AdminAgencyDetail]: "Agency Detail",
  [ADMIN_ROUTES.AgenticLogicStats]: "Agentic Logic Stats",
  [ADMIN_ROUTES.ShippingProviderStats]: "Custom Shipping Provider Stats",
  [ADMIN_ROUTES.BillingWorkflowTrace]: "Billing Workflow Trace",
  [ADMIN_ROUTES.BillingMetrics]: "Billing Metrics",
  [ADMIN_ROUTES.BillingMetricsCommand]: "Billing Metrics - Command",
  [ADMIN_ROUTES.AdminOrgDetail]: "Organization Detail",
  [ADMIN_ROUTES.BillingSupportCommand]: "Billing Support - Command",
  [ADMIN_ROUTES.FeatureFlags]: "Feature Flags",
  [ADMIN_ROUTES.AiPatchReview]: "AI Patch Review",
  [ADMIN_ROUTES.AiPylonTicketAnalysis]: "Pylon Ticket",
  [ADMIN_ROUTES.AiShiftAnalysis]: "Shift Analysis",
  [ADMIN_ROUTES.KnowledgeManagement]: "Knowledge Base",
  [ADMIN_ROUTES.KnowledgeSearch]: "Knowledge Base - Search",
  [ADMIN_ROUTES.KnowledgeDocument]: "Knowledge Base - Document",
  [ADMIN_ROUTES.PylonTicketAnalysis]: "Pylon Ticket",
  [ADMIN_ROUTES.ShiftAnalysis]: "Shift Analysis",
  [ADMIN_ROUTES.Regression]: "Regression",
  [ADMIN_ROUTES.PatchReview]: "Patch Review",
  [ADMIN_ROUTES.SlackIntegrations]: "Slack Integrations",
  [ADMIN_ROUTES.SlackIntegrationDetail]: "Slack Integration",

  // AI
  [AI_ROUTES.Ai]: "AI",
  [AI_ROUTES.AiWorkflowBuilder]: "AI - Workflow Builder",
  [AI_ROUTES.AiWorkflowBuilderWithId]: "AI - Workflow Builder",
  [AI_ROUTES.AiAgents]: "AI - Agents",
  [AI_ROUTES.AiAgentDetail]: "AI - Agent",
  [AI_ROUTES.AiTestCategorizations]: "AI - Test Categorizations",
  [AI_ROUTES.AiBotReview]: "AI - Bot Review",
  [AI_ROUTES.AiAgenticLogic]: "AI - Agentic Logic",
  [AI_ROUTES.AiAgenticLogicNewRule]: "AI - Agentic Logic New Rule",
  [AI_ROUTES.AiAgenticLogicRule]: "AI - Agentic Logic Rule",

  // Public
  [PUBLIC_ROUTES.Login]: "Login",
  [PUBLIC_ROUTES.Share_Id]: "Share",
  [PUBLIC_ROUTES.Install]: "Install",
};
